<template>
  <v-card
    class="form-card glass-card mb-6 animate-form-card"
    id="event-manual-entry-results"
  >
    <!-- Header with JCI Gradient & Live Progress Bar -->
    <div class="form-card-header pa-5 position-relative overflow-hidden">
      <div
        class="d-flex align-center justify-space-between flex-wrap gap-2 position-relative z-index-2"
      >
        <div class="d-flex align-center">
          <div class="header-avatar-circle mr-3">
            <v-icon color="white">mdi-cogs</v-icon>
          </div>
          <div>
            <h2 class="form-card-title">
              {{ Title }} &mdash; Live Workflow Engine
            </h2>
            <p class="form-card-subtitle">
              Real-time multi-stage transaction & notification execution
              pipeline
            </p>
          </div>
        </div>

        <div class="d-flex align-center gap-2" v-if="totalRecords > 0">
          <v-chip
            class="font-weight-bold white--text shadow-sm live-status-chip"
            :color="
              engineHasError
                ? '#EF4444'
                : isEngineFinished
                ? '#56BDA3'
                : '#0097D7'
            "
          >
            <v-icon x-small left color="white" class="mr-1">
              {{
                engineHasError
                  ? "mdi-alert-circle"
                  : isEngineFinished
                  ? "mdi-check-decagram"
                  : "mdi-loading mdi-spin"
              }}
            </v-icon>
            {{
              engineHasError
                ? "EXECUTION FAILED"
                : isEngineFinished
                ? "PROCESS COMPLETED"
                : "PROCESSING WORKFLOW..."
            }}
          </v-chip>
        </div>
      </div>

      <!-- Top Horizontal Live Progress Bar -->
      <div class="top-progress-container mt-4" v-if="totalRecords > 0">
        <div class="d-flex justify-space-between align-center mb-1">
          <span
            class="progress-bar-label white--text text-caption font-weight-bold"
          >
            Workflow Progress
          </span>
          <span class="progress-bar-percentage white--text font-weight-bold">
            {{ activeProgressPercent }}%
          </span>
        </div>
        <div class="progress-track-bg">
          <div
            class="progress-fill-bar"
            :style="`width: ${activeProgressPercent}%;`"
            :class="{
              'fill-completed': isEngineFinished,
              'fill-error': engineHasError,
            }"
          >
            <div class="progress-bar-shine"></div>
          </div>
        </div>
      </div>
    </div>

    <v-card-text class="pa-6">
      <!-- Live Metrics Counter Cards -->
      <v-row class="mb-6" v-if="totalRecords > 0">
        <v-col cols="12" sm="6" md="3" class="py-2">
          <div class="metric-mini-card pa-4 metric-card-1">
            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="metric-label">Total Steps</span>
                <h3 class="metric-val mb-0">{{ totalWorkflowSteps }}</h3>
              </div>
              <v-avatar color="rgba(58, 103, 177, 0.12)" size="42">
                <v-icon color="#3A67B1" small
                  >mdi-format-list-bulleted-square</v-icon
                >
              </v-avatar>
            </div>
          </div>
        </v-col>

        <v-col cols="12" sm="6" md="3" class="py-2">
          <div class="metric-mini-card pa-4 metric-card-2">
            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="metric-label">Completed Steps</span>
                <h3 class="metric-val text-seafoam mb-0">
                  {{ completedStepsCount }}
                </h3>
              </div>
              <v-avatar color="rgba(86, 189, 163, 0.15)" size="42">
                <v-icon color="#56BDA3" small>mdi-check-all</v-icon>
              </v-avatar>
            </div>
          </div>
        </v-col>

        <v-col cols="12" sm="6" md="3" class="py-2">
          <div class="metric-mini-card pa-4 metric-card-3">
            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="metric-label">Remaining Steps</span>
                <h3 class="metric-val text-aqua mb-0">
                  {{ remainingStepsCount }}
                </h3>
              </div>
              <v-avatar color="rgba(0, 151, 215, 0.15)" size="42">
                <v-icon color="#0097D7" small>mdi-progress-clock</v-icon>
              </v-avatar>
            </div>
          </div>
        </v-col>

        <v-col cols="12" sm="6" md="3" class="py-2">
          <div class="metric-mini-card pa-4 metric-card-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <span class="metric-label">Overall Progress</span>
                <h3 class="metric-val text-navy mb-0">
                  {{ activeProgressPercent }}%
                </h3>
              </div>
              <v-avatar color="rgba(237, 190, 56, 0.18)" size="42">
                <v-icon color="#EDBE38" small>mdi-chart-line-variant</v-icon>
              </v-avatar>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Sequential Real-Time Workflow Engine Cards with Live Auto-Scroll -->
      <div class="workflow-engine-panel mb-6" v-if="totalRecords > 0">
        <h3 class="engine-section-title mb-4 d-flex align-center">
          <v-icon color="#3A67B1" class="mr-2" small>mdi-play-circle</v-icon>
          Live Auto-Scrolling Process Execution
        </h3>

        <div class="workflow-steps-list">
          <div
            v-for="(step, idx) in workflowSteps"
            :key="idx"
            :id="`step-card-${idx}`"
            class="workflow-step-card pa-4 mb-4 position-relative"
            :class="`step-state-${getStepStatus(idx)}`"
          >
            <!-- Connecting Progress Line (between steps) -->
            <div
              v-if="idx < workflowSteps.length - 1"
              class="step-connector-line"
              :class="{
                'connector-completed':
                  getStepStatus(idx) === 'completed' ||
                  getStepStatus(idx) === 'skipped',
                'connector-loading': getStepStatus(idx) === 'loading',
                'connector-failed': getStepStatus(idx) === 'failed',
              }"
            ></div>

            <div
              class="d-flex align-center justify-space-between flex-wrap gap-3"
            >
              <!-- Left Side: Animated Icon / Avatar -->
              <div class="d-flex align-center">
                <div
                  class="step-icon-avatar mr-4"
                  :class="`avatar-${getStepStatus(idx)}`"
                >
                  <!-- Loading Spinner -->
                  <div
                    class="spinner-ring"
                    v-if="getStepStatus(idx) === 'loading'"
                  ></div>

                  <!-- Completed Tick Draw SVG Animation -->
                  <svg
                    v-if="getStepStatus(idx) === 'completed'"
                    class="svg-tick-draw"
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="none"
                      stroke="#FFFFFF"
                      stroke-width="3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4 12l5 5L20 6"
                    />
                  </svg>

                  <!-- Skipped / Disabled Icon -->
                  <v-icon
                    v-else-if="getStepStatus(idx) === 'skipped'"
                    color="white"
                    small
                  >
                    mdi-minus-circle-outline
                  </v-icon>

                  <!-- Failed Cross Icon -->
                  <v-icon
                    v-else-if="getStepStatus(idx) === 'failed'"
                    color="white"
                    small
                    class="shake-cross-icon"
                  >
                    mdi-close
                  </v-icon>

                  <!-- Waiting / Icon state -->
                  <v-icon
                    v-else-if="getStepStatus(idx) === 'waiting'"
                    color="#90A4AE"
                    small
                  >
                    {{ step.icon }}
                  </v-icon>

                  <v-icon
                    v-else-if="getStepStatus(idx) === 'loading'"
                    color="#0097D7"
                    small
                    class="spinning-icon"
                  >
                    {{ step.icon }}
                  </v-icon>

                  <!-- Particle burst on completion -->
                  <div
                    class="particle-burst"
                    v-if="getStepStatus(idx) === 'completed'"
                  ></div>
                </div>

                <!-- Center: Title & Description -->
                <div>
                  <h4
                    class="step-title mb-1 font-weight-bold"
                    :class="`title-${getStepStatus(idx)}`"
                  >
                    {{ step.title }}
                  </h4>
                  <p class="step-desc mb-0 text-caption font-weight-medium">
                    {{ step.desc }}
                  </p>
                </div>
              </div>

              <!-- Right Side: Status Badge Pill -->
              <div>
                <v-chip
                  small
                  class="font-weight-bold step-status-pill"
                  :class="`pill-${getStepStatus(idx)}`"
                >
                  <template v-if="getStepStatus(idx) === 'waiting'">
                    <v-icon x-small left color="#90A4AE"
                      >mdi-radiobox-blank</v-icon
                    >
                    Waiting
                  </template>

                  <template v-else-if="getStepStatus(idx) === 'loading'">
                    <v-icon x-small left color="#0097D7" class="mdi-spin"
                      >mdi-loading</v-icon
                    >
                    Processing...
                  </template>

                  <template v-else-if="getStepStatus(idx) === 'completed'">
                    <v-icon x-small left color="#2E7D32"
                      >mdi-check-circle</v-icon
                    >
                    Completed
                  </template>

                  <template v-else-if="getStepStatus(idx) === 'skipped'">
                    <v-icon x-small left color="#78909C"
                      >mdi-minus-circle-outline</v-icon
                    >
                    Disabled in ENV
                  </template>

                  <template v-else-if="getStepStatus(idx) === 'failed'">
                    <v-icon x-small left color="#C62828"
                      >mdi-alert-circle</v-icon
                    >
                    Failed
                  </template>
                </v-chip>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error Callout Box (shown immediately if any process fails) -->
      <v-card
        id="error-details-section"
        class="error-details-card pa-5 mb-6 animate-error-card"
        v-if="activeFailedRecord"
        flat
      >
        <div class="d-flex align-center mb-3">
          <div class="error-avatar-circle mr-3">
            <v-icon color="#EF4444">mdi-alert-octagram</v-icon>
          </div>
          <div>
            <h4 class="error-card-title text-danger mb-0 font-weight-bold">
              Workflow Execution Interrupted
            </h4>
            <span class="text-caption text-muted">
              Member ID: <strong>{{ activeFailedRecord.MembershipId }}</strong>
            </span>
          </div>
        </div>

        <div class="error-body-box pa-4 rounded-lg">
          <p class="error-msg-text mb-0 font-weight-medium">
            <v-icon small color="#EF4444" class="mr-1"
              >mdi-alert-circle-outline</v-icon
            >
            {{
              activeFailedRecord.Error ||
              "An error occurred while processing transaction record."
            }}
          </p>
        </div>
      </v-card>

      <!-- All Processes Completed: Premium Enterprise Dashboard Summary Card with Confetti -->
      <v-card
        id="success-dashboard-section"
        class="success-dashboard-card pa-6 animate-success-dashboard position-relative"
        v-if="isEngineFinished && !engineHasError && activeSuccessRecord"
        flat
      >
        <!-- Lightweight Confetti / Sparkles -->
        <div class="sparkles-wrapper">
          <div class="sparkle-item sp-1">✨</div>
          <div class="sparkle-item sp-2">🎉</div>
          <div class="sparkle-item sp-3">⭐</div>
          <div class="sparkle-item sp-4">✨</div>
          <div class="sparkle-item sp-5">🎉</div>
          <div class="sparkle-item sp-6">⭐</div>
        </div>

        <div class="text-center mb-6">
          <div
            class="success-badge-container d-inline-flex align-center justify-center mb-3"
          >
            <div class="success-badge-pulse"></div>
            <v-avatar
              size="72"
              color="linear-gradient(135deg, #56BDA3 0%, #3A67B1 100%)"
              class="elevation-4"
            >
              <v-icon color="primary" large>mdi-check-decagram</v-icon>
            </v-avatar>
          </div>
          <h2 class="success-main-title font-weight-extrabold mb-1">
            Payment & Registration Successfully Processed!
          </h2>
          <p class="success-sub-title text-muted">
            All database records created, official receipt generated, and
            notification channels processed.
          </p>
        </div>

        <!-- Enterprise Metrics Data Grid -->
        <div class="metrics-grid-box pa-5 mb-4">
          <v-row>
            <v-col cols="12" sm="6" md="4" class="py-2">
              <div class="data-cell">
                <span class="data-label">Member Name</span>
                <h4 class="data-val font-weight-bold text-truncate">
                  {{
                    activeSuccessRecord.MemberName ||
                    activeSuccessRecord.MembershipId
                  }}
                </h4>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4" class="py-2">
              <div class="data-cell">
                <span class="data-label">Membership ID</span>
                <h4 class="data-val font-weight-bold text-navy">
                  {{ activeSuccessRecord.MembershipId }}
                </h4>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4" class="py-2">
              <div class="data-cell">
                <span class="data-label">Receipt Number</span>
                <h4 class="data-val font-weight-bold text-seafoam">
                  {{ activeSuccessRecord.ReceiptNo || "GENERATED" }}
                </h4>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4" class="py-2">
              <div class="data-cell">
                <span class="data-label">Receipt Status</span>
                <v-chip
                  x-small
                  :color="
                    activeSuccessRecord.ReceiptSent ? '#56BDA3' : '#78909C'
                  "
                  class="white--text font-weight-bold mt-1"
                >
                  <v-icon x-small left color="white">
                    {{
                      activeSuccessRecord.ReceiptSent
                        ? "mdi-check-circle"
                        : "mdi-minus-circle-outline"
                    }}
                  </v-icon>
                  {{
                    activeSuccessRecord.ReceiptSent
                      ? "Generated & Sent"
                      : "Generated (Not Sent)"
                  }}
                </v-chip>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4" class="py-2">
              <div class="data-cell">
                <span class="data-label">Email Status</span>
                <v-chip
                  x-small
                  :color="activeSuccessRecord.EmailSent ? '#0097D7' : '#78909C'"
                  class="white--text font-weight-bold mt-1"
                >
                  <v-icon x-small left color="white">
                    {{
                      activeSuccessRecord.EmailSent
                        ? "mdi-email-check"
                        : "mdi-email-off"
                    }}
                  </v-icon>
                  {{
                    activeSuccessRecord.EmailSent
                      ? "Delivered"
                      : "Disabled (Off in ENV)"
                  }}
                </v-chip>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="4" class="py-2">
              <div class="data-cell">
                <span class="data-label">SMS & WhatsApp</span>
                <v-chip
                  x-small
                  :color="
                    activeSuccessRecord.SmsSent ||
                    activeSuccessRecord.WhatsappSent
                      ? '#3A67B1'
                      : '#78909C'
                  "
                  class="white--text font-weight-bold mt-1"
                >
                  <v-icon x-small left color="white">
                    {{
                      activeSuccessRecord.SmsSent ||
                      activeSuccessRecord.WhatsappSent
                        ? "mdi-message-check"
                        : "mdi-message-off"
                    }}
                  </v-icon>
                  {{
                    activeSuccessRecord.SmsSent ||
                    activeSuccessRecord.WhatsappSent
                      ? "Sent & Dispatched"
                      : "Disabled (Off in ENV)"
                  }}
                </v-chip>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card>

      <!-- Bulk Result Cards List (if multiple records processed) -->
      <div class="bulk-results-list mt-6" v-if="Results && Results.length > 1">
        <h4 class="bulk-list-title mb-3 d-flex align-center">
          <v-icon small color="#3A67B1" class="mr-2"
            >mdi-format-list-bulleted</v-icon
          >
          Detailed Individual Record Summary ({{ Results.length }} Records)
        </h4>

        <v-card
          v-for="(item, index) in Results"
          :key="index"
          class="result-card-enterprise mb-3 pa-4"
          :class="item.Uploaded ? 'result-success-card' : 'result-failure-card'"
          flat
        >
          <div
            class="d-flex align-center justify-space-between flex-wrap gap-2"
          >
            <div class="d-flex align-center">
              <v-avatar
                size="40"
                color="#3A67B1"
                class="mr-3 white--text font-weight-bold elevation-1"
              >
                {{ getMemberInitials(item.MemberName || item.MembershipId) }}
              </v-avatar>
              <div>
                <h5 class="member-title mb-0">
                  {{ item.MemberName || item.MembershipId }}
                </h5>
                <span class="text-caption text-muted"
                  >ID: {{ item.MembershipId }}</span
                >
              </div>
            </div>

            <div class="d-flex align-center flex-wrap gap-1">
              <v-chip
                small
                x-small
                :color="item.Uploaded ? '#2E7D32' : '#C62828'"
                class="white--text font-weight-bold"
              >
                {{ item.Uploaded ? "SUCCESSFUL" : "FAILED" }}
              </v-chip>
              <v-chip
                small
                x-small
                color="rgba(58, 103, 177, 0.1)"
                text-color="#3A67B1"
                class="font-weight-bold"
                v-if="item.ReceiptNo"
              >
                Receipt: {{ item.ReceiptNo }}
              </v-chip>
            </div>
          </div>
        </v-card>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "EventManualEntryResults",
  props: {
    Results: {
      type: Array,
      default: () => [],
    },
    Title: {
      type: String,
      default: "Results",
    },
  },
  data() {
    return {
      currentStepIndex: 0,
      isEngineFinished: false,
      engineHasError: false,
      timerId: null,
      executedResultsKey: "",
      workflowSteps: [
        {
          title: "Uploading Payment Information",
          desc: "Registering manual transaction record into Natcon database",
          icon: "mdi-cloud-upload",
        },
        {
          title: "Generating Official Receipt",
          desc: "Compiling GST billing details & generating PDF payment receipt",
          icon: "mdi-receipt",
        },
        {
          title: "Sending Confirmation Email",
          desc: "Dispatching payment receipt PDF link to member email address",
          icon: "mdi-email-check",
        },
        {
          title: "Sending SMS & WhatsApp Alerts",
          desc: "Sending instant mobile SMS & WhatsApp confirmation alerts",
          icon: "mdi-message-processing",
        },
        {
          title: "Finalizing Registration",
          desc: "Completing workflow execution and locking entry record",
          icon: "mdi-check-decagram",
        },
      ],
    };
  },
  computed: {
    totalRecords() {
      return this.Results ? this.Results.length : 0;
    },
    successfulUploads() {
      return this.Results ? this.Results.filter((r) => r.Uploaded).length : 0;
    },
    failedUploads() {
      return this.Results ? this.Results.filter((r) => !r.Uploaded).length : 0;
    },
    receiptsSent() {
      return this.Results
        ? this.Results.filter((r) => r.ReceiptSent).length
        : 0;
    },
    emailsSent() {
      return this.Results ? this.Results.filter((r) => r.EmailSent).length : 0;
    },
    smsSent() {
      return this.Results ? this.Results.filter((r) => r.SmsSent).length : 0;
    },
    whatsappSent() {
      return this.Results
        ? this.Results.filter((r) => r.WhatsappSent).length
        : 0;
    },
    successRate() {
      if (!this.totalRecords) return 0;
      return Math.round((this.successfulUploads / this.totalRecords) * 100);
    },
    overallStatus() {
      if (this.totalRecords === 0) return "none";
      if (this.failedUploads === 0) return "success";
      if (this.successfulUploads === 0) return "error";
      return "warning";
    },
    totalWorkflowSteps() {
      return this.workflowSteps.length;
    },
    completedStepsCount() {
      if (this.engineHasError) {
        return Math.max(0, this.currentStepIndex);
      }
      if (this.isEngineFinished) {
        return this.workflowSteps.length;
      }
      return Math.min(this.workflowSteps.length, this.currentStepIndex);
    },
    remainingStepsCount() {
      if (this.isEngineFinished && !this.engineHasError) {
        return 0;
      }
      return Math.max(0, this.workflowSteps.length - this.completedStepsCount);
    },
    activeProgressPercent() {
      if (this.workflowSteps.length === 0) return 0;
      if (this.isEngineFinished && !this.engineHasError) return 100;
      return Math.min(
        100,
        Math.round((this.completedStepsCount / this.workflowSteps.length) * 100)
      );
    },
    activeFailedRecord() {
      if (!this.Results || this.Results.length === 0) return null;
      return this.Results.find((r) => !r.Uploaded || r.Error);
    },
    activeSuccessRecord() {
      if (!this.Results || this.Results.length === 0) return null;
      return this.Results.find((r) => r.Uploaded);
    },
  },
  watch: {
    Results: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          var key = JSON.stringify(newVal);
          if (this.executedResultsKey !== key) {
            this.executedResultsKey = key;
            this.runLiveWorkflowEngine();
          }
        } else {
          this.executedResultsKey = "";
        }
      },
    },
  },
  methods: {
    runLiveWorkflowEngine() {
      if (this.timerId) {
        clearInterval(this.timerId);
      }

      this.currentStepIndex = 0;
      this.isEngineFinished = false;
      this.engineHasError = false;

      // Smooth scroll to initial step
      this.scrollToStep(0);

      var firstFailed = this.Results.find((r) => !r.Uploaded);
      var self = this;

      this.timerId = setInterval(function () {
        if (firstFailed && self.currentStepIndex === 0) {
          self.engineHasError = true;
          self.isEngineFinished = true;
          clearInterval(self.timerId);
          self.scrollToErrorCard();
          return;
        }

        if (self.currentStepIndex < self.workflowSteps.length - 1) {
          self.currentStepIndex++;
          self.scrollToStep(self.currentStepIndex);
        } else {
          self.currentStepIndex = self.workflowSteps.length;
          self.isEngineFinished = true;
          clearInterval(self.timerId);
          self.scrollToSuccessDashboard();
        }
      }, 650);
    },

    scrollToStep(idx) {
      this.$nextTick(function () {
        var el = document.getElementById("step-card-" + idx);
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    },

    scrollToSuccessDashboard() {
      this.$nextTick(function () {
        var el = document.getElementById("success-dashboard-section");
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    },

    scrollToErrorCard() {
      this.$nextTick(function () {
        var el = document.getElementById("error-details-section");
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    },

    getStepStatus(idx) {
      if (this.engineHasError) {
        if (idx === 0) return "failed";
        return "waiting";
      }

      var activeRec = this.activeSuccessRecord;

      if (this.isEngineFinished) {
        if (!activeRec) return "completed";

        if (idx === 1 && !activeRec.ReceiptSent && !activeRec.ReceiptNo) {
          return "skipped";
        }
        if (idx === 2 && !activeRec.EmailSent) {
          return "skipped";
        }
        if (idx === 3 && !activeRec.SmsSent && !activeRec.WhatsappSent) {
          return "skipped";
        }
        return "completed";
      }

      if (idx < this.currentStepIndex) {
        if (activeRec) {
          if (idx === 1 && !activeRec.ReceiptSent && !activeRec.ReceiptNo)
            return "skipped";
          if (idx === 2 && !activeRec.EmailSent) return "skipped";
          if (idx === 3 && !activeRec.SmsSent && !activeRec.WhatsappSent)
            return "skipped";
        }
        return "completed";
      } else if (idx === this.currentStepIndex) {
        return "loading";
      } else {
        return "waiting";
      }
    },

    getMemberInitials(name) {
      if (!name) return "M";
      var parts = name.trim().split(" ");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.slice(0, 2).toUpperCase();
    },
  },
  beforeDestroy() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap");

#event-manual-entry-results {
  font-family: "Outfit", "Plus Jakarta Sans", sans-serif;
  background: #ffffff !important;
  border: 1px solid rgba(58, 103, 177, 0.12) !important;
  border-radius: 20px !important;
  box-shadow: 0 10px 30px rgba(33, 33, 33, 0.05) !important;
  overflow: hidden;
}

.form-card-header {
  background: linear-gradient(90deg, #3a67b1 0%, #0097d7 100%);
  color: white;
}

.header-orb {
  position: absolute;
  border-radius: 50%;
  opacity: 0.15;
  pointer-events: none;
}
.orb-1 {
  width: 140px;
  height: 140px;
  background: #edbe38;
  top: -40px;
  right: -40px;
}
.orb-2 {
  width: 120px;
  height: 120px;
  background: #56bda3;
  bottom: -30px;
  left: 10%;
}

.header-avatar-circle {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.22);
  border: 1.5px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.form-card-title {
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin-bottom: 2px;
}

.form-card-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.88);
  margin-bottom: 0;
}

/* Top Progress Bar */
.top-progress-container {
  background: rgba(0, 0, 0, 0.12);
  padding: 8px 14px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
}

.progress-track-bg {
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.progress-fill-bar {
  height: 100%;
  background: linear-gradient(90deg, #edbe38 0%, #56bda3 100%);
  border-radius: 10px;
  transition: width 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  position: relative;
}

.progress-fill-bar.fill-completed {
  background: linear-gradient(90deg, #56bda3 0%, #2e7d32 100%);
}

.progress-fill-bar.fill-error {
  background: linear-gradient(90deg, #ef4444 0%, #c62828 100%);
}

.progress-bar-shine {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 100%
  );
  animation: shineMove 1.8s infinite linear;
}

/* Metric Mini Cards */
.metric-mini-card {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(58, 103, 177, 0.12);
  border-radius: 14px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.02);
  transition: box-shadow 0.3s ease !important;
}

.metric-mini-card:hover {
  box-shadow: 0 12px 25px rgba(58, 103, 177, 0.14) !important;
}

.metric-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #78909c;
  letter-spacing: 0.5px;
  display: block;
}

.metric-val {
  font-size: 22px;
  font-weight: 800;
  color: #263238;
}

.text-seafoam {
  color: #56bda3 !important;
}
.text-aqua {
  color: #0097d7 !important;
}
.text-navy {
  color: #3a67b1 !important;
}
.text-danger {
  color: #ef4444 !important;
}

/* Workflow Stepper Panel */
.engine-section-title {
  font-size: 14px;
  font-weight: 700;
  color: #3a67b1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.workflow-step-card {
  background: rgba(255, 255, 255, 0.95);
  border: 1.5px solid rgba(58, 103, 177, 0.12);
  border-radius: 14px;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.step-state-loading {
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(0, 151, 215, 0.08) 100%
  ) !important;
  border: 2px solid #0097d7 !important;
  box-shadow: 0 10px 30px rgba(0, 151, 215, 0.28) !important;
  transform: scale(1.025) !important;
  animation: activeStepPulse 1.8s infinite ease-in-out;
  z-index: 5;
  opacity: 1 !important;
  filter: none !important;
}

.step-state-waiting {
  opacity: 0.45 !important;
  filter: grayscale(40%) !important;
  transition: all 0.4s ease;
}

.step-state-completed {
  opacity: 1 !important;
  filter: none !important;
  background: rgba(86, 189, 163, 0.05) !important;
  border: 1.5px solid rgba(86, 189, 163, 0.35) !important;
  box-shadow: 0 6px 18px rgba(86, 189, 163, 0.1) !important;
}

.step-state-skipped {
  opacity: 0.65 !important;
  background: #f8fafc !important;
  border: 1.5px dashed #cfd8dc !important;
}

.step-state-failed {
  background: rgba(239, 68, 68, 0.04) !important;
  border: 1.5px solid rgba(239, 68, 68, 0.35) !important;
  box-shadow: 0 8px 22px rgba(239, 68, 68, 0.12) !important;
}

/* Connector Line */
.step-connector-line {
  position: absolute;
  left: 35px;
  bottom: -20px;
  width: 3px;
  height: 20px;
  background: #cfd8dc;
  z-index: 2;
  transition: background 0.4s ease;
}

.connector-completed {
  background: #56bda3 !important;
}
.connector-loading {
  background: linear-gradient(180deg, #3a67b1 0%, #0097d7 100%) !important;
}
.connector-failed {
  background: #ef4444 !important;
}

/* Step Icon Avatars */
.step-icon-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
}

.avatar-waiting {
  background: #f0f4f8;
  border: 1.5px solid #cfd8dc;
}

.avatar-loading {
  background: rgba(0, 151, 215, 0.12);
  border: 2px solid #0097d7;
  box-shadow: 0 0 14px rgba(0, 151, 215, 0.3);
}

.avatar-completed {
  background: linear-gradient(135deg, #56bda3 0%, #2e7d32 100%);
  box-shadow: 0 4px 14px rgba(86, 189, 163, 0.4);
  animation: tickScalePop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

.avatar-skipped {
  background: #cfd8dc !important;
  box-shadow: none !important;
}

.avatar-failed {
  background: linear-gradient(135deg, #ef4444 0%, #c62828 100%);
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.4);
}

/* SVG Tick Draw Animation */
.svg-tick-draw {
  width: 22px;
  height: 22px;
}

.svg-tick-draw path {
  stroke-dasharray: 40;
  stroke-dashoffset: 40;
  animation: tickStrokeDraw 0.4s ease forwards 0.1s;
}

.spinner-ring {
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  border: 2.5px solid transparent;
  border-top-color: #0097d7;
  border-right-color: #3a67b1;
  animation: spinnerRotate 0.9s infinite linear;
}

.spinning-icon {
  animation: iconPulse 1.2s infinite ease-in-out;
}

.shake-cross-icon {
  animation: shake 1.2s infinite ease-in-out;
}

.particle-burst {
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid rgba(86, 189, 163, 0.6);
  animation: particleBurst 0.6s ease-out forwards;
}

/* Step Titles */
.step-title {
  font-size: 15px;
  font-weight: 800 !important;
}
.title-waiting {
  color: #1e293b !important;
}
.title-loading {
  color: #0097d7 !important;
}
.title-completed {
  color: #0f172a !important;
}
.title-skipped {
  color: #334155 !important;
}
.title-failed {
  color: #ef4444 !important;
}
.step-desc {
  color: #334155 !important;
  font-weight: 600 !important;
  font-size: 13px !important;
}

/* Status Pill Badges */
.step-status-pill {
  padding: 4px 12px !important;
}
.pill-waiting {
  background: #f0f4f8 !important;
  color: #78909c !important;
}
.pill-loading {
  background: rgba(0, 151, 215, 0.12) !important;
  color: #0097d7 !important;
}
.pill-completed {
  background: rgba(86, 189, 163, 0.15) !important;
  color: #2e7d32 !important;
}
.pill-skipped {
  background: #eceff1 !important;
  color: #78909c !important;
}
.pill-failed {
  background: rgba(239, 68, 68, 0.15) !important;
  color: #c62828 !important;
}

/* Error Callout Card */
.error-details-card {
  background: rgba(239, 68, 68, 0.05) !important;
  border: 1.5px solid rgba(239, 68, 68, 0.3) !important;
  border-radius: 16px !important;
}

.error-avatar-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-body-box {
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.error-msg-text {
  color: #c62828;
}

/* Success Dashboard Card */
.success-dashboard-card {
  background: linear-gradient(
    180deg,
    rgba(86, 189, 163, 0.06) 0%,
    rgba(58, 103, 177, 0.03) 100%
  ) !important;
  border: 1.5px solid rgba(86, 189, 163, 0.3) !important;
  border-radius: 20px !important;
  position: relative;
  overflow: hidden;
}

.success-orb {
  position: absolute;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: #56bda3;
  opacity: 0.08;
  top: -50px;
  left: 50%;
  transform: translateX(-50%);
}

.success-badge-container {
  position: relative;
}

.success-badge-pulse {
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  border: 2px solid rgba(86, 189, 163, 0.5);
  animation: particleBurst 1.8s infinite ease-out;
}

.success-main-title {
  font-size: 22px;
  color: #263238;
  letter-spacing: -0.3px;
}

.metrics-grid-box {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(58, 103, 177, 0.12);
  border-radius: 14px;
}

.data-cell {
  padding: 4px 8px;
}

.data-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #78909c;
  letter-spacing: 0.5px;
  display: block;
}

.data-val {
  font-size: 15px;
  color: #263238;
}

/* Keyframes */
@keyframes activeStepPulse {
  0%,
  100% {
    box-shadow: 0 10px 30px rgba(0, 151, 215, 0.25);
    border-color: #0097d7;
  }
  50% {
    box-shadow: 0 14px 40px rgba(0, 151, 215, 0.45);
    border-color: #3a67b1;
  }
}

.sparkles-wrapper {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 10;
}

.sparkle-item {
  position: absolute;
  font-size: 18px;
  animation: floatSparkle 3s infinite ease-in-out;
}

.sp-1 {
  top: 10%;
  left: 8%;
  animation-delay: 0s;
}
.sp-2 {
  top: 15%;
  right: 10%;
  animation-delay: 0.4s;
}
.sp-3 {
  bottom: 20%;
  left: 12%;
  animation-delay: 0.8s;
}
.sp-4 {
  bottom: 15%;
  right: 14%;
  animation-delay: 1.2s;
}
.sp-5 {
  top: 40%;
  left: 4%;
  animation-delay: 1.6s;
}
.sp-6 {
  top: 45%;
  right: 6%;
  animation-delay: 2s;
}

@keyframes floatSparkle {
  0%,
  100% {
    transform: translateY(0) scale(0.8) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-15px) scale(1.3) rotate(15deg);
    opacity: 1;
  }
}

@keyframes shineMove {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

@keyframes spinnerRotate {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes tickStrokeDraw {
  0% {
    stroke-dashoffset: 40;
  }
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes tickScalePop {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  60% {
    transform: scale(1.15);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes particleBurst {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
}

@keyframes iconPulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20%,
  60% {
    transform: translateX(-3px);
  }
  40%,
  80% {
    transform: translateX(3px);
  }
}

/* Mobile Responsive Media Queries */
@media (max-width: 600px) {
  .metric-val {
    font-size: 18px !important;
  }
  .step-title {
    font-size: 13px !important;
  }
  .step-desc {
    font-size: 11px !important;
  }
  .step-icon-avatar {
    width: 36px !important;
    height: 36px !important;
  }
  .step-connector-line {
    left: 31px !important;
  }
  .success-main-title {
    font-size: 18px !important;
  }
  .live-status-chip {
    font-size: 10px !important;
  }
}
</style>
